import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Hand, Clock, ChevronDown, Search, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import Footer from "../components/common/Footer";
import DateTimeSelector from "../components/common/DateTimeSelector";
import ServiceSelector from "../components/common/ServiceSelector";
import Header from "../components/common/Header";
import LocationDropdown from "../components/common/LocationDropdown";
import SalonCarousel from "../components/home/SalonCarousel";
import VisionCarousel from "../components/home/VisionCarousel";
import BenefitsSection from "../components/home/BenefitsSection";
import CityLinksSection from "../components/home/CityLinksSection";
import ActivityTicker from "../components/home/ActivityTicker";
import { Helmet } from 'react-helmet-async';
import { buildDateTimeParams } from '../lib/convertDateTimeToISO';
import { useToast } from "../components/common/Toast";
import { HomePageSkeleton } from "../components/common/Skeletons";
import useVisitorCity from "../hooks/useVisitorCity";

// Fallback when Cloudflare gives no usable hint (no proxy, VPN, unknown IP).
const DEFAULT_CITY = "София";

export default function Home() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation('home');
  const lang = i18n.language;
  const { t: tDatetime } = useTranslation('datetime');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDateTime, setSelectedDateTime] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(DEFAULT_CITY);
  // Guards the async city resolution from overwriting a manual choice made
  // before /api/map/visitor-city answered. This is state (not a ref) because
  // it's read during render below, and refs can't be read during render
  // (react-hooks/refs) — it's only ever written from the onSelect handler,
  // so the extra render this causes is the same one setSelectedLocation
  // already triggers.
  const [locationTouched, setLocationTouched] = useState(false);
  const visitorCity = useVisitorCity();
  // Tracks which visitorCity value we've already adopted, so the render-time
  // sync below only fires once per newly-resolved value (mirrors the old
  // effect's [visitorCity] dependency array).
  const [adoptedVisitorCity, setAdoptedVisitorCity] = useState(null);
  const [sections, setSections] = useState([]);
  const [sectionsError, setSectionsError] = useState(false);
  const [homeData, setHomeData] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${import.meta.env.VITE_REACT_APP_API_URL}/api/home`, { signal: controller.signal })
      .then(r => r.json())
      .then(data => {
        if (!data.success) { setSectionsError(true); return; }
        setHomeData(data.data);
        const seen = new Set();
        const unique = [];
        for (const p of data.data.professions) {
          if (p.section_id && !seen.has(p.section_id)) {
            seen.add(p.section_id);
            unique.push({
              id: p.section_id,
              name: p.section_name,
              name_en: p.section_name_en,
              name_ru: p.section_name_ru,
            });
          }
        }
        setSections(unique);
      })
      .catch((err) => { if (err.name !== 'AbortError') setSectionsError(true); });
    return () => controller.abort();
  }, []);

  // Adopt the IP-resolved city as the default. Fires the first time the hook
  // resolves a value; never overrides a user selection made via LocationDropdown.
  // ServiceSelector re-fetches on selectedCity, so the service list follows.
  // Done during render (not in an effect) per React's guidance for "adjusting
  // state when a prop/derived value changes" — avoids the extra
  // effect-triggered re-render that react-hooks/set-state-in-effect flags.
  if (visitorCity && visitorCity !== adoptedVisitorCity && !locationTouched) {
    setAdoptedVisitorCity(visitorCity);
    setSelectedLocation(visitorCity);
  }

  const serviceRef = useRef(null);
  const dateRef = useRef(null);
  const locationRef = useRef(null);

  const toggleDropdown = (name) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inLocation = locationRef.current?.contains(event.target);
      const inService = serviceRef.current?.contains(event.target);
      const inDate = dateRef.current?.contains(event.target);
      if (!inLocation && !inService && !inDate) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = () => {
    if (!selectedLocation) return toast.warning(t('location_label'));
    const params = new URLSearchParams();

    if (typeof selectedLocation === "object" && selectedLocation.type === "location") {
      const { latitude, longitude, radius } = selectedLocation.data;
      params.append("latitude", latitude);
      params.append("longitude", longitude);
      params.append("radius", radius);
      if (selectedService) params.append("service_id", selectedService.id);
      if (selectedDateTime) {
        const dt = buildDateTimeParams(selectedDateTime, tDatetime);
        if (dt) {
          params.append("date", dt.date);
          if (dt.start_time && dt.end_time) {
            params.append("start_time", dt.start_time);
            params.append("end_time", dt.end_time);
          }
        }
      }
      navigate(`/search?${params.toString()}`);
    } else {
      const locationValue =
        typeof selectedLocation === "string" ? selectedLocation : selectedLocation.data?.city;
      params.append("city", locationValue);
      if (selectedService) params.append("service_id", selectedService.id);
      if (selectedDateTime) {
        const dt = buildDateTimeParams(selectedDateTime, tDatetime);
        if (dt) {
          params.append("date", dt.date);
          if (dt.start_time && dt.end_time) {
            params.append("start_time", dt.start_time);
            params.append("end_time", dt.end_time);
          }
        }
      }
      navigate(`/search?${params.toString()}`);
    }
  };

  const getLocationDisplayText = () => {
    if (!selectedLocation) return visitorCity || DEFAULT_CITY;
    if (typeof selectedLocation === "object" && selectedLocation.type === "location") {
      return selectedLocation.data.address?.split(",")[0] || t('around_me', { ns: 'location' });
    }
    return selectedLocation;
  };

  return (
    <>
      <Helmet>
        <title>{t('home_meta_title')}</title>
        <meta name="description" content={t('home_meta_desc')} />
        <link rel="canonical" href="https://salonite365.bg/" />
        <link rel="alternate" hrefLang="bg" href="https://salonite365.bg/" />
        <meta property="og:title" content={t('home_meta_title')} />
        <meta property="og:description" content={t('home_meta_desc')} />
        <meta property="og:url" content="https://salonite365.bg/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="bg_BG" />
        <meta property="og:image" content="https://salonite365.bg/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": "https://salonite365.bg/#website",
              "url": "https://salonite365.bg/",
              "name": "Salonite365",
              "description": "Платформа за резервации на салони за красота в България",
              "inLanguage": "bg",
              "potentialAction": {
                "@type": "SearchAction",
                "target": { "@type": "EntryPoint", "urlTemplate": "https://salonite365.bg/search?city={search_term_string}" },
                "query-input": "required name=search_term_string"
              }
            },
            {
              "@type": "Organization",
              "@id": "https://salonite365.bg/#organization",
              "name": "Salonite365",
              "url": "https://salonite365.bg/",
              "logo": { "@type": "ImageObject", "url": "https://salonite365.bg/icon-512.png" },
              "contactPoint": { "@type": "ContactPoint", "email": "support@salonite365.bg", "contactType": "customer service" },
              "sameAs": []
            }
          ]
        })}</script>
      </Helmet>
      <div className="min-h-screen font-sans bg-white">
        {/* ─── HERO SECTION ──────────────────────────────────────────────── */}
        <section className="relative flex flex-col" style={{ background: "#82b2c0" }}>
          {/* Background image */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=1920&q=85&auto=format&fit=crop"
              alt="Salon background"
              className="w-full h-full object-cover"
              fetchPriority="high"
              decoding="async"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(105deg, rgba(130,178,192,0.82) 0%, rgba(195,222,221,0.65) 45%, rgba(240,226,195,0.45) 100%)",
              }}
            />
          </div>

          {/* Header floats on top */}
          <Header />

          {/* Salon owner CTA banner */}
          <div
            className="relative z-20 w-full border-b border-white/15 bg-stone-700/30 backdrop-blur-md"
            style={{ marginTop: "64px" }}
          >
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2.5 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-white/90 text-xs sm:text-sm font-medium leading-snug">
                {t('owner_banner_text')}
              </p>
              <a
                href="/addsalon"
                className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold px-4 py-1.5 rounded-full bg-white text-stone-800 shadow-sm hover:bg-stone-100 hover:shadow-md hover:-translate-y-px active:translate-y-0 transition-all duration-200 whitespace-nowrap no-underline"
              >
                {t('owner_banner_cta')}
                <span aria-hidden="true" className="text-[0.8em]">→</span>
              </a>
            </div>
          </div>

          {/* Hero content */}
          <div className="relative z-10">
            <div className="w-full max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                <div className="flex flex-col justify-center">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="text-white/80 text-xs uppercase tracking-[0.25em] font-medium">
                      {t('tagline')}
                    </p>
                  </div>

                  {/* Search card */}
                  <div
                    className="bg-white rounded-3xl overflow-visible shadow-2xl flex items-stretch"
                    style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.35)" }}
                  >
                    <div className="p-7 pb-7 flex-1">
                      <h2
                        className="text-stone-900 text-2xl font-black mb-1 leading-tight"
                        style={{ letterSpacing: "-0.02em" }}
                      >
                        {t('hero_title')}
                      </h2>
                      <p className="text-stone-600 text-sm mb-6">
                        {t('hero_subtitle')}
                      </p>

                      {/* Location */}
                      <div
                        ref={locationRef}
                        className="relative"
                        style={{ zIndex: openDropdown === "location" ? 200 : 30 }}
                      >
                        <div className="flex items-center gap-3 py-3 border-b border-stone-100">
                          <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
                            style={{ background: "rgba(255,199,95,0.2)" }}>
                            <MapPin className="w-5 h-5" style={{ color: "#ffc75f" }} />
                          </div>
                          <LocationDropdown
                            selectedLocation={selectedLocation}
                            isOpen={openDropdown === "location"}
                            onToggle={() => toggleDropdown("location")}
                           onSelect={(loc) => {
                              setLocationTouched(true);
                              setSelectedLocation(loc);
                              setOpenDropdown(null);
                            }}
                          />
                        </div>
                      </div>

                      {/* Service */}
                      <div
                        ref={serviceRef}
                        className="relative"
                        style={{ zIndex: openDropdown === "service" ? 190 : 20 }}
                      >
                        <div
                          className="flex items-center gap-3 py-3 border-b border-stone-100 cursor-pointer"
                          onClick={() => toggleDropdown("service")}
                        >
                          <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
                            style={{ background: "rgba(163,70,213,0.12)" }}>
                            <Hand className="w-5 h-5" style={{ color: "#a346d5" }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-bold text-stone-600 uppercase tracking-widest">
                              {t('service_label')}
                            </p>
                            <p className={`text-sm font-semibold truncate ${selectedService ? "text-stone-800" : "text-stone-600"}`}>
                              {selectedService?.name || t('service_placeholder')}
                            </p>
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 text-stone-400 transition-transform flex-shrink-0 ${
                              openDropdown === "service" ? "rotate-180" : ""
                            }`}
                          />
                        </div>
                        <ServiceSelector
                          isOpen={openDropdown === "service"}
                          onClose={() => setOpenDropdown(null)}
                          onSelect={(service) => {
                            setSelectedService(service);
                            setOpenDropdown(null);
                          }}
                          selectedCity={getLocationDisplayText()}
                          isLocationBased={
                            typeof selectedLocation === "object" &&
                            selectedLocation.type === "location"
                          }
                        />
                      </div>

                      {/* Date */}
                      <div
                        ref={dateRef}
                        className="relative"
                        style={{ zIndex: openDropdown === "date" ? 180 : 10 }}
                      >
                        <div
                          className="flex items-center gap-3 py-3 cursor-pointer"
                          onClick={() => toggleDropdown("date")}
                        >
                          <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
                            style={{ background: "rgba(0,210,252,0.15)" }}>
                            <Clock className="w-5 h-5" style={{ color: "#009efa" }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-bold text-stone-600 uppercase tracking-widest">
                              {t('datetime_label')}
                            </p>
                            <p className={`text-sm font-semibold truncate ${selectedDateTime ? "text-stone-800" : "text-stone-600"}`}>
                              {selectedDateTime || t('datetime_placeholder')}
                            </p>
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 text-stone-400 transition-transform flex-shrink-0 ${
                              openDropdown === "date" ? "rotate-180" : ""
                            }`}
                          />
                        </div>
                        <DateTimeSelector
                          isOpen={openDropdown === "date"}
                          onClose={() => setOpenDropdown(null)}
                          onSelect={(dateTime) => {
                            setSelectedDateTime(dateTime);
                            setOpenDropdown(null);
                          }}
                        />
                      </div>
                    </div>

                    <div className="py-4 pr-4 flex-shrink-0">
                      <button
                        onClick={handleSearch}
                        aria-label={t('search')}
                        className="flex items-center justify-center w-14 h-full rounded-2xl text-white transition-all duration-200 disabled:opacity-50"
                        style={{
                          background: "linear-gradient(180deg, #d1b7f3 0%, #a346d5 100%)",
                          boxShadow: "0 8px 24px rgba(163,70,213,0.35)",
                        }}
                      >
                        <Search className="w-7 h-7" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <ActivityTicker />
                </div>

                {/* RIGHT: Hero headline */}
                <div className="hidden lg:flex flex-col justify-center pl-8">
                  <div
                    className="leading-[0.92] font-black mb-6"
                    style={{
                      fontSize: "clamp(3rem, 5vw, 4.5rem)",
                      letterSpacing: "-0.03em",
                      color: "#1a3a42",
                      textShadow: "0 2px 20px rgba(255,255,255,0.2)",
                    }}
                  >
                    <span className="block">{t('hero_headline_1')}</span>
                    <span className="block">{t('hero_headline_2')}</span>
                    <span className="block" style={{
                      backgroundImage: "linear-gradient(90deg, #ef6997 0%, #e85cba 50%, #b84cdc 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}>
                      {t('hero_headline_3')}
                    </span>
                    <span className="block">{t('hero_headline_4')}</span>
                  </div>
                  <p className="text-[#1a3a42]/70 text-lg max-w-sm leading-relaxed font-medium">
                    {t('hero_description')}
                  </p>

                  {/* Decorative pill tags — real sections from API */}
                  <div className="flex flex-wrap gap-2 mt-8">
                    {sectionsError ? (
                      <span
                        className="px-4 py-1.5 rounded-full text-sm font-semibold border"
                        style={{ background: "rgba(255,255,255,0.3)", borderColor: "rgba(244,165,138,0.5)", color: "#1a3a42", backdropFilter: "blur(6px)" }}
                      >
                        {t('sections_load_error', { defaultValue: 'Услугите не могат да се заредят' })}
                      </span>
                    ) : sections.map((section) => (
                      <span
                        key={section.id}
                        className="px-4 py-1.5 rounded-full text-sm font-semibold border cursor-pointer transition"
                        style={{
                          background: "rgba(255,255,255,0.45)",
                          borderColor: "rgba(163,70,213,0.35)",
                          color: "#1a3a42",
                          backdropFilter: "blur(6px)",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(163,70,213,0.14)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.45)"; }}
                        onClick={() => {
                          const params = new URLSearchParams();
                          if (typeof selectedLocation === "object" && selectedLocation.type === "location") {
                            const { latitude, longitude, radius } = selectedLocation.data;
                            params.append("latitude", latitude);
                            params.append("longitude", longitude);
                            params.append("radius", radius);
                          } else {
                            params.append(
                              "city",
                              typeof selectedLocation === "string"
                                ? selectedLocation
                                : selectedLocation.data?.city || visitorCity || DEFAULT_CITY
                            );
                          }
                          params.append("section_id", section.id);
                          navigate(`/search?${params.toString()}`);
                        }}
                      >
                        {(lang === 'en' && section.name_en) || (lang === 'ru' && section.name_ru) || section.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </section>

        <style>{`
          @keyframes scrollDot {
            0%, 100% { transform: translateY(0); opacity: 1; }
            50% { transform: translateY(12px); opacity: 0.3; }
          }
        `}</style>

        {/* ─── PLATFORM DESCRIPTION (Google OAuth verification) ───── */}
        {/* <section className="bg-white py-10 px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-4">
              Salonite365 — A platform for booking beauty salons in Bulgaria
            </h1>
            <p className="text-stone-600 text-sm sm:text-base leading-relaxed mb-3">
              Salonite365 is a platform for discovering and booking beauty salons in Bulgaria. We use Google authentication to securely log you into your account and manage your appointments.
            </p>
            <p className="text-stone-500 text-xs">
              <a href="/privacy" className="underline hover:text-stone-700 transition-colors">
                Privacy Policy
              </a>
              {' · '}
              <a href="/terms" className="underline hover:text-stone-700 transition-colors">
                Terms of Service
              </a>
            </p>
          </div>
        </section> */}

        {homeData ? (
          <>
            <SalonCarousel initialData={homeData.featuredSalons}/>
            <BenefitsSection />
            <VisionCarousel initialData={homeData.gallery} />
            <CityLinksSection initialCities={homeData.cityStats} initialPopularServices={homeData.popularServices} />
          </>
        ) : (
          <HomePageSkeleton />
        )}
        <Footer />
      </div>
    </>
  );
}